import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  parseFractionLikeInput as parseFraction,
  formatLatexPreview,
} from "../../../components/inputs/FractionInput";

const pick = (items) => items[Math.floor(Math.random() * items.length)];
const stripUnits = (raw) => String(raw || "").replace(/[^\d.,/-]+/g, "");
const parseAngle = (raw) => parseFraction(stripUnits(raw));
const degreeLatex = (value) => `${value}^{\\circ}`;
const ALPHA = "\\alpha";
const SVG_ALPHA = "\u03b1";
const DEGREE_SYMBOL = "\u00b0";
const PARALLEL_SYMBOL = "\u2225";

const ORANGE = "#ff9d00";
const PURPLE = "#d946ef";
const LINE = "var(--color-text, #f8fafc)";
const FILL = "color-mix(in srgb, var(--color-accent, #ff9d00) 12%, transparent)";
const MATH_FONT = "Cambria Math, STIX Two Math, Georgia, 'Times New Roman', serif";
const POINT_LABEL_SIZE = 18;
const ANGLE_LABEL_SIZE = 16;
const ANGLE_STROKE = 3;

const QUAD_VERTICES = ["A", "B", "C", "D"];

function InlineAngle({ vertex }) {
  return (
    <span style={{ fontFamily: MATH_FONT, fontStyle: "italic", whiteSpace: "nowrap" }}>
      {"\u2220"}{vertex}
    </span>
  );
}

const TASK_SLOTS = [
  "QuadrilateralSumAngleTask",
  "TrapezoidLegAnglesTask",
  "TrapezoidExpressionAnglesTask",
  "ParallelogramAdjacentAnglesTask",
  "CyclicQuadrilateralOppositeAnglesTask",
  "CyclicQuadrilateralEquationTask",
];
const TAGS = ["planimetria", "czworokaty", "katy-w-czworokatach"];

const variants = {
  sum360: [
    { a: 82, b: 94, c: 103 },
    { a: 74, b: 118, c: 86 },
    { a: 96, b: 72, c: 121 },
    { a: 67, b: 113, c: 108 },
    { a: 105, b: 88, c: 76 },
  ].map((item) => ({ ...item, answer: 360 - item.a - item.b - item.c })),
  trapezoidLeg: [
    { knownVertex: "A", askVertex: "D", known: 64, answer: 116 },
    { knownVertex: "D", askVertex: "A", known: 72, answer: 108 },
    { knownVertex: "B", askVertex: "C", known: 58, answer: 122 },
    { knownVertex: "C", askVertex: "B", known: 111, answer: 69 },
    { knownVertex: "A", askVertex: "D", known: 83, answer: 97 },
    { knownVertex: "B", askVertex: "C", known: 76, answer: 104 },
  ],
  trapezoidExpression: [
    { bottomCoef: 1, bottomConst: 20, topCoef: 2, topConst: -5 },
    { bottomCoef: 2, bottomConst: 10, topCoef: 1, topConst: 35 },
    { bottomCoef: 3, bottomConst: -10, topCoef: 1, topConst: 30 },
    { bottomCoef: 2, bottomConst: -20, topCoef: 2, topConst: 40 },
  ].map((item) => ({
    ...item,
    answer: (180 - item.bottomConst - item.topConst) / (item.bottomCoef + item.topCoef),
  })),
  parallelogramAdjacent: [
    { leftCoef: 2, leftConst: 15, rightCoef: 1, rightConst: 30 },
    { leftCoef: 3, leftConst: -20, rightCoef: 1, rightConst: 40 },
    { leftCoef: 2, leftConst: -10, rightCoef: 2, rightConst: 30 },
    { leftCoef: 1, leftConst: 25, rightCoef: 3, rightConst: -5 },
  ].map((item) => ({
    ...item,
    answer: (180 - item.leftConst - item.rightConst) / (item.leftCoef + item.rightCoef),
  })),
  cyclicOpposite: [2, 3, 4].map((factor) => ({
    factor,
    answer: 180 / (factor + 1),
    opposite: (180 / (factor + 1)) * factor,
  })),
  cyclicEquation: [
    { firstCoef: 1, firstConst: 20, secondCoef: 2, secondConst: -20 },
    { firstCoef: 2, firstConst: 10, secondCoef: 1, secondConst: 20 },
    { firstCoef: 3, firstConst: -30, secondCoef: 1, secondConst: 10 },
    { firstCoef: 2, firstConst: -10, secondCoef: 2, secondConst: 30 },
  ].map((item) => ({
    ...item,
    answer: (180 - item.firstConst - item.secondConst) / (item.firstCoef + item.secondCoef),
  })),
};

function typeToMode(type) {
  if (type === "QuadrilateralSumAngleTask") return "sum360";
  if (type === "TrapezoidLegAnglesTask") return "trapezoidLeg";
  if (type === "TrapezoidExpressionAnglesTask") return "trapezoidExpression";
  if (type === "ParallelogramAdjacentAnglesTask") return "parallelogramAdjacent";
  if (type === "CyclicQuadrilateralOppositeAnglesTask") return "cyclicOpposite";
  if (type === "CyclicQuadrilateralEquationTask") return "cyclicEquation";
  return pick(["sum360", "trapezoidLeg", "trapezoidExpression", "parallelogramAdjacent", "cyclicOpposite", "cyclicEquation"]);
}

function answer(data) {
  return data.answer;
}

function signedTermLatex(value) {
  if (!value) return "";
  return value > 0 ? "+" + value + "^{\\circ}" : String(value) + "^{\\circ}";
}

function coefficientLatex(coef) {
  return coef === 1 ? ALPHA : String(coef) + ALPHA;
}

function linearLatex(coef, constant = 0) {
  return coefficientLatex(coef) + signedTermLatex(constant);
}

function expressionSvg(coef, constant = 0) {
  const alphaPart = coef === 1 ? SVG_ALPHA : String(coef) + SVG_ALPHA;
  return !constant ? alphaPart : alphaPart + (constant > 0 ? "+" : "") + constant + DEGREE_SYMBOL;
}

function statement(data) {
  if (data.mode === "sum360") {
    return (
      <>
        W czworokącie <InlineMath math="ABCD" /> trzy kąty mają miary{" "}
        <InlineMath math={degreeLatex(data.a)} />, <InlineMath math={degreeLatex(data.b)} /> oraz{" "}
        <InlineMath math={degreeLatex(data.c)} />. Oblicz miarę kąta oznaczonego{" "}
        <InlineMath math={ALPHA} />.
      </>
    );
  }
  if (data.mode === "trapezoidLeg") {
    return (
      <>
        W trapezie <InlineMath math="ABCD" /> podstawy <InlineMath math="AB" /> i{" "}
        <InlineMath math="CD" /> są równoległe. Kąt przy wierzchołku{" "}
        <InlineMath math={data.knownVertex} /> ma miarę <InlineMath math={degreeLatex(data.known)} />.
        Oblicz miarę kąta oznaczonego <InlineMath math={ALPHA} /> przy wierzchołku{" "}
        <InlineMath math={data.askVertex} />.
      </>
    );
  }
  if (data.mode === "trapezoidExpression") {
    return (
      <>
        W trapezie <InlineMath math="ABCD" /> podstawy <InlineMath math="AB" /> i{" "}
        <InlineMath math="CD" /> są równoległe. Kąty przy jednym ramieniu mają miary{" "}
        <InlineMath math={linearLatex(data.bottomCoef, data.bottomConst)} /> oraz{" "}
        <InlineMath math={linearLatex(data.topCoef, data.topConst)} />. Oblicz <InlineMath math={ALPHA} />.
      </>
    );
  }
  if (data.mode === "parallelogramAdjacent") {
    return (
      <>
        W równoległoboku <InlineMath math="ABCD" /> miary dwóch sąsiednich kątów opisano wyrażeniami{" "}
        <InlineMath math={linearLatex(data.leftCoef, data.leftConst)} /> oraz{" "}
        <InlineMath math={linearLatex(data.rightCoef, data.rightConst)} />. Oblicz <InlineMath math={ALPHA} />.
      </>
    );
  }
  if (data.mode === "cyclicEquation") {
    return (
      <>
        Czworokąt <InlineMath math="ABCD" /> jest wpisany w okrąg. Miary przeciwległych kątów{" "}
        <InlineAngle vertex="A" /> i <InlineAngle vertex="C" /> opisano wyrażeniami{" "}
        <InlineMath math={linearLatex(data.firstCoef, data.firstConst)} /> oraz{" "}
        <InlineMath math={linearLatex(data.secondCoef, data.secondConst)} />. Oblicz <InlineMath math={ALPHA} />.
      </>
    );
  }
  return (
    <>
      Czworokąt <InlineMath math="ABCD" /> jest wpisany w okrąg. Miara kąta{" "}
      <InlineAngle vertex="C" /> jest <InlineMath math={String(data.factor)} /> razy większa od miary
      przeciwległego kąta <InlineAngle vertex="A" />. Oblicz miarę mniejszego z tych kątów,
      oznaczonego <InlineMath math={ALPHA} />.
    </>
  );
}

function explanationSteps(data) {
  if (data.mode === "sum360") {
    return [
      {
        text: "Suma kątów wewnętrznych każdego czworokąta wynosi 360°.",
        math: "\\angle A+\\angle B+\\angle C+\\angle D=360^{\\circ}",
      },
      {
        text: "Odejmujemy od 360° miary trzech podanych kątów.",
        math: `${ALPHA}=360^{\\circ}-${data.a}^{\\circ}-${data.b}^{\\circ}-${data.c}^{\\circ}=${degreeLatex(data.answer)}`,
      },
    ];
  }
  if (data.mode === "trapezoidLeg") {
    return [
      {
        text: "Podstawy trapezu są równoległe. Ramię trapezu przecina te podstawy, więc kąty przy tym samym ramieniu uzupełniają się do 180°.",
        math: `${ALPHA}+${degreeLatex(data.known)}=180^{\\circ}`,
      },
      {
        text: "Odejmujemy podany kąt od 180°.",
        math: `${ALPHA}=180^{\\circ}-${degreeLatex(data.known)}=${degreeLatex(data.answer)}`,
      },
    ];
  }
  if (data.mode === "trapezoidExpression") {
    return [
      {
        text: "Kąty przy tym samym ramieniu trapezu uzupełniają się do 180°.",
        math: `${linearLatex(data.bottomCoef, data.bottomConst)}+${linearLatex(data.topCoef, data.topConst)}=180^{\\circ}`,
      },
      {
        text: "Rozwiązujemy równanie liniowe z niewiadomą α.",
        math: `${data.bottomCoef + data.topCoef}${ALPHA}${signedTermLatex(data.bottomConst + data.topConst)}=180^{\\circ}\\quad\\Rightarrow\\quad${ALPHA}=${degreeLatex(data.answer)}`,
      },
    ];
  }
  if (data.mode === "parallelogramAdjacent") {
    return [
      {
        text: "W równoległoboku kąty sąsiednie uzupełniają się do 180°.",
        math: `${linearLatex(data.leftCoef, data.leftConst)}+${linearLatex(data.rightCoef, data.rightConst)}=180^{\\circ}`,
      },
      {
        text: "Po zebraniu wyrazów podobnych otrzymujemy wartość α.",
        math: `${data.leftCoef + data.rightCoef}${ALPHA}${signedTermLatex(data.leftConst + data.rightConst)}=180^{\\circ}\\quad\\Rightarrow\\quad${ALPHA}=${degreeLatex(data.answer)}`,
      },
    ];
  }
  if (data.mode === "cyclicEquation") {
    return [
      {
        text: "W czworokącie wpisanym w okrąg kąty przeciwległe mają sumę 180°.",
        math: `${linearLatex(data.firstCoef, data.firstConst)}+${linearLatex(data.secondCoef, data.secondConst)}=180^{\\circ}`,
      },
      {
        text: "Rozwiązujemy równanie i wyznaczamy α.",
        math: `${data.firstCoef + data.secondCoef}${ALPHA}${signedTermLatex(data.firstConst + data.secondConst)}=180^{\\circ}\\quad\\Rightarrow\\quad${ALPHA}=${degreeLatex(data.answer)}`,
      },
    ];
  }
  return [
    {
      text: "W czworokącie wpisanym w okrąg kąty przeciwległe mają sumę 180°.",
      math: `${ALPHA}+${data.factor}${ALPHA}=180^{\\circ}`,
    },
    {
      text: "Dodajemy wyrazy podobne i dzielimy przez współczynnik przy α.",
      math: `${data.factor + 1}${ALPHA}=180^{\\circ}\\quad\\Rightarrow\\quad${ALPHA}=${degreeLatex(data.answer)}`,
    },
  ];
}
function toRad(deg) {
  return (deg * Math.PI) / 180;
}

function pointAt(vertex, angle, radius) {
  return {
    x: vertex.x + Math.cos(toRad(angle)) * radius,
    y: vertex.y + Math.sin(toRad(angle)) * radius,
  };
}

function angleDeg(vertex, point) {
  return (Math.atan2(point.y - vertex.y, point.x - vertex.x) * 180) / Math.PI;
}

function normalizeSweep(start, end) {
  let sweep = (end - start) % 360;
  if (sweep < 0) sweep += 360;
  return sweep;
}

function arcPath(vertex, startAngle, endAngle, radius) {
  const start = pointAt(vertex, startAngle, radius);
  const end = pointAt(vertex, endAngle, radius);
  const sweep = normalizeSweep(startAngle, endAngle);
  return `M ${start.x.toFixed(1)} ${start.y.toFixed(1)} A ${radius} ${radius} 0 ${sweep > 180 ? 1 : 0} 1 ${end.x.toFixed(1)} ${end.y.toFixed(1)}`;
}

function midArcPoint(vertex, startAngle, endAngle, radius) {
  const sweep = normalizeSweep(startAngle, endAngle);
  return pointAt(vertex, startAngle + sweep / 2, radius);
}

function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function centroid(points) {
  return {
    x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
    y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
  };
}

function intersectRays(originA, angleA, originB, angleB) {
  const a = { x: Math.cos(toRad(angleA)), y: Math.sin(toRad(angleA)) };
  const b = { x: Math.cos(toRad(angleB)), y: Math.sin(toRad(angleB)) };
  const cross = a.x * b.y - a.y * b.x;
  if (Math.abs(cross) < 0.0001) return null;

  const diff = { x: originB.x - originA.x, y: originB.y - originA.y };
  const t = (diff.x * b.y - diff.y * b.x) / cross;
  return { x: originA.x + t * a.x, y: originA.y + t * a.y };
}

function fitPointsToViewBox(points, width = 360, height = 210, padding = 56) {
  const minX = Math.min(...points.map((point) => point.x));
  const maxX = Math.max(...points.map((point) => point.x));
  const minY = Math.min(...points.map((point) => point.y));
  const maxY = Math.max(...points.map((point) => point.y));
  const scale = Math.min((width - 2 * padding) / (maxX - minX || 1), (height - 2 * padding) / (maxY - minY || 1));
  const usedWidth = (maxX - minX) * scale;
  const usedHeight = (maxY - minY) * scale;
  const left = (width - usedWidth) / 2;
  const top = (height - usedHeight) / 2;

  return points.map((point) => ({
    x: left + (point.x - minX) * scale,
    y: top + (point.y - minY) * scale,
  }));
}

function permutations(items) {
  if (items.length <= 1) return [items];
  return items.flatMap((item, index) =>
    permutations([...items.slice(0, index), ...items.slice(index + 1)]).map((rest) => [item, ...rest])
  );
}

function getQuadrilateralAngleOptions(data) {
  const unknownVertex = data.unknownVertex || "D";
  return permutations([data.a, data.b, data.c]).map((knownAngles) => {
    let knownIndex = 0;
    return Object.fromEntries(
      QUAD_VERTICES.map((vertex) => {
        if (vertex === unknownVertex) return [vertex, data.answer];
        const value = knownAngles[knownIndex];
        knownIndex += 1;
        return [vertex, value];
      })
    );
  });
}
function scoreQuadrilateral(points) {
  const minX = Math.min(...points.map((point) => point.x));
  const maxX = Math.max(...points.map((point) => point.x));
  const minY = Math.min(...points.map((point) => point.y));
  const maxY = Math.max(...points.map((point) => point.y));
  const width = maxX - minX;
  const height = maxY - minY;
  const sides = points.map((point, index) => distance(point, points[(index + 1) % points.length]));
  return Math.min(width, height) * 1.5 + Math.min(...sides);
}

function buildQuadrilateralCandidate(angles, baseLength, sideLength) {
  const A0 = { x: 0, y: 0 };
  const B0 = { x: baseLength, y: 0 };
  const dirBC = 180 - angles.B;
  const C0 = {
    x: B0.x + Math.cos(toRad(dirBC)) * sideLength,
    y: B0.y + Math.sin(toRad(dirBC)) * sideLength,
  };
  const D0 = intersectRays(A0, angles.A, C0, dirBC + 180 - angles.C);
  if (!D0 || D0.y <= 20) return null;
  const screenPoints = [A0, B0, C0, D0].map((point) => ({ x: point.x, y: -point.y }));
  return fitPointsToViewBox(screenPoints, 360, 210, 42);
}

function buildQuadrilateralPoints(data) {
  let best = null;
  getQuadrilateralAngleOptions(data).forEach((angles) => {
    [150, 170, 190, 210, 230].forEach((baseLength) => {
      [82, 102, 122, 142, 162, 182].forEach((sideLength) => {
        const points = buildQuadrilateralCandidate(angles, baseLength, sideLength);
        if (!points) return;
        const value = scoreQuadrilateral(points);
        if (!best || value > best.value) best = { points, angles, value };
      });
    });
  });

  const fallbackAngles = getQuadrilateralAngleOptions(data)[0];
  const points = best?.points || fitPointsToViewBox([
    { x: 0, y: 0 },
    { x: 165, y: 0 },
    { x: 190, y: -92 },
    { x: 28, y: -102 },
  ], 360, 210, 42);
  const [A, B, C, D] = points;
  return { A, B, C, D, angles: best?.angles || fallbackAngles };
}

function outwardLabel(point, inside, amount = 24) {
  const dx = point.x - inside.x;
  const dy = point.y - inside.y;
  const length = Math.hypot(dx, dy) || 1;
  return { x: point.x + (dx / length) * amount, y: point.y + (dy / length) * amount };
}
function circlePoint(center, radius, angle) {
  return pointAt(center, angle, radius);
}

function pointString(points) {
  return points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
}

function SvgMathLabel({ x, y, children, color = LINE, size = POINT_LABEL_SIZE, anchor = "middle" }) {
  return (
    <text
      x={x}
      y={y}
      fill={color}
      fontFamily={MATH_FONT}
      fontStyle="italic"
      fontSize={size}
      textAnchor={anchor}
      dominantBaseline="middle"
    >
      {children}
    </text>
  );
}

function AngleMark({ vertex, from, to, color, label, radius = 21, offset = 14, inside, labelSize = ANGLE_LABEL_SIZE }) {
  let start = angleDeg(vertex, from);
  let end = angleDeg(vertex, to);
  if (inside) {
    const forwardSweep = normalizeSweep(start, end);
    const backwardSweep = normalizeSweep(end, start);
    const forwardMid = pointAt(vertex, start + forwardSweep / 2, radius);
    const backwardMid = pointAt(vertex, end + backwardSweep / 2, radius);
    if (distance(backwardMid, inside) < distance(forwardMid, inside)) {
      start = angleDeg(vertex, to);
      end = angleDeg(vertex, from);
    }
  }
  const labelPoint = midArcPoint(vertex, start, end, radius + offset);
  return (
    <>
      <path
        d={arcPath(vertex, start, end, radius)}
        fill="none"
        stroke={color}
        strokeWidth={ANGLE_STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <SvgMathLabel x={labelPoint.x} y={labelPoint.y} color={color} size={labelSize}>
        {label}
      </SvgMathLabel>
    </>
  );
}

function QuadDiagram({ data }) {
  if (data.mode === "trapezoidLeg" || data.mode === "trapezoidExpression") return <TrapezoidAnglesDiagram data={data} />;
  if (data.mode === "parallelogramAdjacent") return <ParallelogramAnglesDiagram data={data} />;
  if (data.mode === "cyclicOpposite" || data.mode === "cyclicEquation") return <CyclicQuadrilateralDiagram data={data} />;
  return <QuadrilateralSumDiagram data={data} />;
}

function QuadrilateralSumDiagram({ data }) {
  const { A, B, C, D, angles } = buildQuadrilateralPoints(data);
  const points = { A, B, C, D };
  const inside = centroid([A, B, C, D]);
  const unknownVertex = data.unknownVertex || "D";
  const angleLabel = (vertex) => (vertex === unknownVertex ? SVG_ALPHA : `${angles[vertex]}${DEGREE_SYMBOL}`);
  const angleColor = (vertex) => (vertex === unknownVertex ? PURPLE : ORANGE);
  return (
    <svg viewBox="0 0 360 210" style={{ width: "100%", maxWidth: 420, display: "block", margin: "12px auto" }} role="img" aria-label={"Czworok\u0105t z zaznaczonymi k\u0105tami"}>
      <polygon points={pointString([A, B, C, D])} fill={FILL} stroke={LINE} strokeWidth="4" strokeLinejoin="round" />
      {QUAD_VERTICES.map((vertex, index) => {
        const previous = points[QUAD_VERTICES[(index + QUAD_VERTICES.length - 1) % QUAD_VERTICES.length]];
        const next = points[QUAD_VERTICES[(index + 1) % QUAD_VERTICES.length]];
        return (
          <AngleMark
            key={vertex}
            vertex={points[vertex]}
            from={next}
            to={previous}
            label={angleLabel(vertex)}
            color={angleColor(vertex)}
            radius={17}
            offset={18}
            inside={inside}
            labelSize={14}
          />
        );
      })}
      {QUAD_VERTICES.map((vertex) => {
        const labelPoint = outwardLabel(points[vertex], inside);
        return <SvgMathLabel key={`label-${vertex}`} x={labelPoint.x} y={labelPoint.y}>{vertex}</SvgMathLabel>;
      })}
    </svg>
  );
}

function TrapezoidAnglesDiagram({ data }) {
  const isExpression = data.mode === "trapezoidExpression";
  const expressionBottom = isExpression ? data.bottomCoef * data.answer + data.bottomConst : null;
  const leftA = isExpression ? expressionBottom : data.knownVertex === "A" ? data.known : data.askVertex === "A" ? data.answer : 72;
  const rightB = data.knownVertex === "B" ? data.known : data.askVertex === "B" ? data.answer : 66;
  const h = 88;
  const A = { x: 54, y: 154 };
  const B = { x: 306, y: 154 };
  const D = isExpression ? { x: 102, y: 154 - h } : { x: 54 + h / Math.tan(toRad(leftA)), y: 154 - h };
  const C = isExpression ? { x: 260, y: 154 - h } : { x: 306 - h / Math.tan(toRad(rightB)), y: 154 - h };
  const inside = centroid([A, B, C, D]);
  const labelFor = (vertex) => {
    if (isExpression) {
      if (vertex === "A") return expressionSvg(data.bottomCoef, data.bottomConst);
      if (vertex === "D") return expressionSvg(data.topCoef, data.topConst);
      return null;
    }
    return data.knownVertex === vertex ? data.known + DEGREE_SYMBOL : data.askVertex === vertex ? SVG_ALPHA : null;
  };
  const colorFor = (vertex) => (isExpression || data.askVertex === vertex ? PURPLE : ORANGE);
  return (
    <svg viewBox="0 0 360 210" style={{ width: "100%", maxWidth: 390, display: "block", margin: "12px auto" }} role="img" aria-label={"Trapez z zaznaczonymi kątami"}>
      <polygon points={pointString([A, B, C, D])} fill={FILL} stroke={LINE} strokeWidth="4" strokeLinejoin="round" />
      <SvgMathLabel x={180} y={126} color={ORANGE} size={16}>{`AB ${PARALLEL_SYMBOL} CD`}</SvgMathLabel>
      {labelFor("A") && <AngleMark vertex={A} from={B} to={D} label={labelFor("A")} color={colorFor("A")} inside={inside} radius={17} offset={isExpression ? 26 : 18} labelSize={14} />}
      {labelFor("B") && <AngleMark vertex={B} from={C} to={A} label={labelFor("B")} color={colorFor("B")} inside={inside} radius={17} offset={18} labelSize={14} />}
      {labelFor("C") && <AngleMark vertex={C} from={D} to={B} label={labelFor("C")} color={colorFor("C")} inside={inside} radius={17} offset={18} labelSize={14} />}
      {labelFor("D") && <AngleMark vertex={D} from={A} to={C} label={labelFor("D")} color={colorFor("D")} inside={inside} radius={17} offset={isExpression ? 26 : 18} labelSize={14} />}
      <SvgMathLabel x={A.x - 17} y={A.y + 22}>A</SvgMathLabel>
      <SvgMathLabel x={B.x + 17} y={B.y + 22}>B</SvgMathLabel>
      <SvgMathLabel x={C.x + 16} y={C.y - 16}>C</SvgMathLabel>
      <SvgMathLabel x={D.x - 16} y={D.y - 16}>D</SvgMathLabel>
    </svg>
  );
}

function ParallelogramAnglesDiagram({ data }) {
  const A = { x: 64, y: 154 };
  const B = { x: 286, y: 154 };
  const D = { x: 116, y: 70 };
  const C = { x: 338, y: 70 };
  const inside = centroid([A, B, C, D]);
  const points = { A, B, C, D };
  return (
    <svg viewBox="0 0 390 220" style={{ width: "100%", maxWidth: 410, display: "block", margin: "12px auto" }} role="img" aria-label={"Równoległobok z zaznaczonymi kątami"}>
      <polygon points={pointString([A, B, C, D])} fill={FILL} stroke={LINE} strokeWidth="4" strokeLinejoin="round" />
      <AngleMark vertex={A} from={B} to={D} label={expressionSvg(data.leftCoef, data.leftConst)} color={PURPLE} inside={inside} radius={18} offset={18} labelSize={14} />
      <AngleMark vertex={B} from={C} to={A} label={expressionSvg(data.rightCoef, data.rightConst)} color={PURPLE} inside={inside} radius={18} offset={18} labelSize={14} />
      {QUAD_VERTICES.map((vertex) => {
        const labelPoint = outwardLabel(points[vertex], inside);
        return <SvgMathLabel key={vertex} x={labelPoint.x} y={labelPoint.y}>{vertex}</SvgMathLabel>;
      })}
    </svg>
  );
}
function splitArc(total, bias = 0.5) {
  const first = Math.max(34, Math.min(total - 34, total * bias));
  return [first, total - first];
}

function circleLabelPoint(center, point, amount = 28) {
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  const length = Math.hypot(dx, dy) || 1;
  return { x: point.x + (dx / length) * amount, y: point.y + (dy / length) * amount };
}

function CyclicQuadrilateralDiagram({ data }) {
  const center = { x: 180, y: 126 };
  const radius = 96;
  const alpha = data.answer;
  const isEquation = data.mode === "cyclicEquation";
  const firstAngle = isEquation ? data.firstCoef * alpha + data.firstConst : alpha;
  const secondAngle = isEquation ? data.secondCoef * alpha + data.secondConst : data.factor * alpha;
  const smallArc = 2 * firstAngle;
  const largeArc = 2 * secondAngle;
  const [bcArc, cdArc] = splitArc(smallArc, 0.56);
  const abArc = splitArc(largeArc, 0.46)[1];
  const start = 205;
  const A = circlePoint(center, radius, start);
  const B = circlePoint(center, radius, start + abArc);
  const C = circlePoint(center, radius, start + abArc + bcArc);
  const D = circlePoint(center, radius, start + abArc + bcArc + cdArc);
  const points = { A, B, C, D };
  const inside = centroid([A, B, C, D]);
  const labelA = isEquation ? expressionSvg(data.firstCoef, data.firstConst) : SVG_ALPHA;
  const labelC = isEquation ? expressionSvg(data.secondCoef, data.secondConst) : String(data.factor) + SVG_ALPHA;
  return (
    <svg viewBox="0 0 360 270" style={{ width: "100%", maxWidth: 440, display: "block", margin: "12px auto" }} role="img" aria-label={"Czworokąt wpisany w okrąg"}>
      <circle cx={center.x} cy={center.y} r={radius} fill="none" stroke={LINE} strokeWidth="4" />
      <polygon points={pointString([A, B, C, D])} fill={FILL} stroke={LINE} strokeWidth="4" strokeLinejoin="round" />
      <AngleMark vertex={A} from={B} to={D} label={labelA} color={PURPLE} radius={18} offset={16} inside={inside} labelSize={15} />
      <AngleMark vertex={C} from={D} to={B} label={labelC} color={ORANGE} radius={18} offset={16} inside={inside} labelSize={15} />
      {QUAD_VERTICES.map((vertex) => {
        const labelPoint = circleLabelPoint(center, points[vertex], 32);
        return <SvgMathLabel key={`circle-label-${vertex}`} x={labelPoint.x} y={labelPoint.y}>{vertex}</SvgMathLabel>;
      })}
    </svg>
  );
}
function AngleInput({ userAnswer, onChange, disabled }) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  return (
    <FractionInput
      value={raw}
      onChange={(value) => onChange({ raw: value?.raw ?? "", parsed: parseAngle(value?.raw ?? "") })}
      disabled={disabled}
      label={ALPHA}
      suffixLatex={String.raw`{}^\circ`}
      placeholder="np. 65"
    />
  );
}

export function generateTask(type) {
  const taskType = TASK_SLOTS.includes(type) ? type : pick(TASK_SLOTS);
  const mode = typeToMode(taskType);
  const baseData = { ...pick(variants[mode]), mode };
  if (mode === "sum360") baseData.unknownVertex = pick(QUAD_VERTICES);
  return { type: taskType, data: baseData, tags: TAGS };
}

export function generateTasks() {
  return TASK_SLOTS.map((type) => generateTask(type));
}

export function renderTask(task) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.65 }}>
      <p>{statement(task.data)}</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div>
      <div style={{ fontWeight: 700, marginBottom: 6 }}><InlineMath math={`${ALPHA} =`} /></div>
      <AngleInput userAnswer={userAnswer} onChange={onChange} disabled={disabled} />
    </div>
  );
}

export function checkAnswer(task, userAnswer) {
  const parsed = typeof userAnswer === "object" && Number.isFinite(userAnswer?.parsed)
    ? userAnswer.parsed
    : parseAngle(typeof userAnswer === "string" ? userAnswer : userAnswer?.raw);
  return Number.isFinite(parsed) && Math.abs(parsed - answer(task.data)) < 0.01;
}

export function renderAnswer(task) {
  return <InlineMath math={`${ALPHA} = ${degreeLatex(answer(task.data))}`} />;
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  if (!raw) return <i>(brak odpowiedzi)</i>;
  const parsed = parseAngle(raw);
  if (Number.isFinite(parsed)) {
    return <InlineMath math={`${ALPHA} = ${degreeLatex(parsed)}`} />;
  }
  return <InlineMath math={`${ALPHA} = ${formatLatexPreview(raw)}\\,{}^{\\circ}`} />;
}

export function renderExplanation(task, options = {}) {
  const showDiagram = options.showDiagram !== false;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.8 }}>
      {showDiagram && <QuadDiagram data={task.data} />}
      {explanationSteps(task.data).map((step, index) => (
        <React.Fragment key={`${task.data.mode}-${index}`}>
          <p><strong>Krok {index + 1}.</strong> {step.text}</p>
          <BlockMath math={step.math} />
        </React.Fragment>
      ))}
      <p><strong>Odpowiedź.</strong> <InlineMath math={`${ALPHA} = ${degreeLatex(answer(task.data))}`} />.</p>
    </div>
  );
}

export const quadrilateralAnglesRegistry = Object.fromEntries(
  TASK_SLOTS.map((type) => [
    type,
    {
      renderTask: (task) => renderTask({ ...task, type }),
      renderInput: (task, userAnswer, onChange, disabled) => renderInput({ ...task, type }, userAnswer, onChange, disabled),
      checkAnswer: (task, userAnswer) => checkAnswer({ ...task, type }, userAnswer),
      renderAnswer: (task) => renderAnswer({ ...task, type }),
      renderExplanation: (task) => renderExplanation({ ...task, type }),
      renderUserAnswer: (userAnswer, task) => renderUserAnswer(userAnswer, { ...task, type }),
    },
  ])
);

export const registry = quadrilateralAnglesRegistry;

const quadrilateralAnglesModule = {
  generateTask,
  generateTasks,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
  registry,
};

export default quadrilateralAnglesModule;
